import { useGlobalQuery } from '@/state/query-client/hooks';
import { getFiles } from '@/lib/api/services/storage.service';
import type { StorageFileItem } from '@/lib/api/types/storage.types';
import { createApplicationHooks } from '@/modules/service-requests/shared/application-hooks';
import {
  changeIdCardStatus,
  idCardApi,
  submitIdCardApplication,
} from '../services/id-card.service';
import { IdCardApplication } from '../types/id-card.types';

const hooks = createApplicationHooks<IdCardApplication>({
  key: 'id-card',
  api: idCardApi,
  withdrawWorkflow: 'ID_CARD_APPLICATION_WITHDRAW',
});

export const useIdCardApplications = hooks.useApplications;
export const useIdCardApplication = hooks.useApplication;
export const useWithdrawIdCard = hooks.useWithdrawApplication;

export const useSubmitIdCard = () =>
  hooks.useApplicationMutation(submitIdCardApplication, 'REQUEST_SUBMITTED');

export const useChangeIdCardStatus = () =>
  hooks.useApplicationMutation(changeIdCardStatus, 'REQUEST_UPDATED');

type ResolvedFile = { itemId: string; url: string };

const resolved = (files?: StorageFileItem[] | null): ResolvedFile[] =>
  (files ?? []).filter((file): file is ResolvedFile => Boolean(file.itemId && file.url));

/**
 * The files storage can resolve. One bulk read normally; when storage refuses one id (a missing or
 * foreign file) the whole bulk read fails, so the ids are then read one by one and the bad ones
 * skipped, like the legacy service, instead of losing every link on the page.
 */
const readFiles = async (fileIds: string[]): Promise<ResolvedFile[]> => {
  try {
    return resolved((await getFiles(fileIds)).data);
  } catch {
    const results = await Promise.allSettled(fileIds.map((id) => getFiles([id])));
    return results.flatMap((result) =>
      result.status === 'fulfilled' ? resolved(result.value.data) : []
    );
  }
};

/**
 * File id -> signed URL for the photos and receipts on the given page of applications. They are
 * Private files whose URLs expire after about 5 minutes, so they are re-read well before that.
 */
export const fetchIdCardFileUrls = async (items: IdCardApplication[]) => {
  const fileIds = [
    ...new Set(items.flatMap((item) => [item.PhotoId, item.PaymentReferenceId])),
  ].filter((id): id is string => Boolean(id));
  if (!fileIds.length) return {};
  return Object.fromEntries((await readFiles(fileIds)).map((file) => [file.itemId, file.url]));
};

export const useIdCardFileUrls = (items: IdCardApplication[]) =>
  useGlobalQuery({
    queryKey: ['hr-service-desk', 'id-card', 'files', items.map((item) => item.ItemId)],
    queryFn: () => fetchIdCardFileUrls(items),
    enabled: items.length > 0,
    staleTime: 2 * 60 * 1000,
    refetchInterval: 3 * 60 * 1000,
  });
